import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  QueryList,
  TemplateRef,
  ViewChild,
  ViewChildren,
} from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { MatExpansionPanel } from "@angular/material/expansion";
import { MatPaginator } from "@angular/material/paginator";
import { MatSlideToggle } from "@angular/material/slide-toggle";
import { MatSort } from "@angular/material/sort";
import { MatTable } from "@angular/material/table";
import { ActivatedRoute, NavigationEnd, Router, RouterEvent } from "@angular/router";
import { faUserEdit } from "@fortawesome/free-solid-svg-icons";
import moment from "moment-timezone";
import { Subject } from "rxjs";
import { filter, takeUntil } from "rxjs/operators";
import { AccountRelatedDTO } from "src/app/accounts/Account";
import { AccountService } from "src/app/accounts/account.service";
import { AccountUser, AccountUserDTO } from "src/app/accounts/AccountUser";
import { UserDTO } from "src/app/auth/User";
import { FacilityDTO } from "src/app/facilities/Facility";
import { NotificationService } from "src/app/notifications/notification.service";
import { PermissionService } from "src/app/permission.service";
import { SessionService } from "src/app/session.service";
import { AreYouSureDialogComponent } from "src/app/shared/dialogs/are-you-sure-dialog/are-you-sure-dialog.component";
import { UiService } from "src/app/ui/ui.service";
import { UsersService } from "../users.service";

@Component({
  selector: "app-preferences",
  templateUrl: "./preferences.component.html",
  styleUrls: ["./preferences.component.scss"],
})
export class PreferencesComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(MatSlideToggle) superSlideToggle!: MatSlideToggle;
  @ViewChild("accountsTable") accountsTable!: MatTable<AccountUserDTO[]>;
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild("template") template!: TemplateRef<any>;
  @ViewChildren(MatExpansionPanel)
  expansionChildren!: QueryList<MatExpansionPanel>;
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSlideToggle) nurtureSlideToggle!: MatSlideToggle;

  user?: UserDTO;
  userPreferencesForm!: FormGroup;
  userDeleted = false;
  sendInternalEmails = false;
  displayedColumns = ["account", "role", "groups", "edit"];
  faUserEdit = faUserEdit;
  accountUsers!: AccountUser[];
  timeZoneOptions: { name: string; value: string }[];
  availableAccounts: UserAccount[] | undefined;
  accountUserForm!: FormGroup;
  selectedGroupsObjArr: any[] | undefined;
  selectedFacilities?: number[] = [];
  accountUserFormChanged?: boolean;
  isNewUser!: boolean;
  isSelf!:boolean;
  showAccountAccess: boolean | undefined;
  showBilling: boolean = false;
  showDelete!: boolean;
  basicRole: any;
  private onComponentDestroy = new Subject();

  constructor(
    private sessionService: SessionService,
    private usersService: UsersService,
    private notificationService: NotificationService,
    private accountService: AccountService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private dialog: MatDialog,
    private permissionService: PermissionService,
    private cdRef: ChangeDetectorRef,
    private uiService: UiService,
  ) {
    this.timeZoneOptions = moment.tz.names().map((name) => {
      return {
        name: name.replace("/", ": ").replace("_", " "),
        value: name,
      };
    });
  }

  get currentUser() {
    return this.sessionService.currentUserValue;
  }

  get currentAccount() {
    return this.sessionService.currentAccountValue!;
  }

  get openedAccount() {
    return this.availableAccounts?.find((account) => {
      return account.expanded;
    });
  }

  get accountUserChangesDisabled() {
    return this.currentUser?.id === this.user?.id && !this.currentUser?.super;
  }

  get canActivate() {
    if(this.isNewUser || this.isSelf || this.user?.disabled || this.user?.deleted_at !== null || this.user?.activation_token !== null){
      return false;
    }
    return true;
  }

  get activateDisabled() {
    if(!this.user?.super && this.user?.accountUsers.length == 0){
      return true;
    }
    return false;
  }

  get isSuperDisabled() {
    return false;
  }

  get showBasicRoleSelect() {
    if(!this.currentUser?.super && this.availableAccounts?.length == 1){
      return true;
    }
    return false;
  }

  get basicRoles() {
    if(this.availableAccounts){
      return this.availableAccounts![0].accountRelatedDTO.roles;
    }
    return [];
  }

  ngOnInit(): void {

    // On param change, set user
    this.activatedRoute.params
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe(() => {
        this.initForm(this.activatedRoute.snapshot.data.user);
      });

    this.showAccountAccess = true; //this.currentUser?.super
    this.showBilling = this.currentAccount.accountRelatedDTO.stripeCustomers.length > 0;

    Promise.all([this.permissionService.categories.users.hasRemove()]).then(
      ([hasRemove]) => {
        this.showDelete = hasRemove;
      },
    );

  }

  initForm(user: any) {
    console.log('initForm')
    this.user = user;
    this.isNewUser = !this.user;
    this.isSelf = this.user ? this.user.id === this.currentUser!.id : false;
    this.initAccountUsers();
    this.initPreferencesForm();
  }

  getControl() {
    return typeof this.userPreferencesForm?.controls.email;
  }

  ngAfterViewInit() {}

  ngOnDestroy() {
    this.onComponentDestroy.next();
    this.onComponentDestroy.complete();
  }

  initAccountUsers() {
    this.accountUsers = this.user?.accountUsers
      ? this.user?.accountUsers?.map((accountUser) => {
          return new AccountUser(accountUser);
        })
      : [];

    this.availableAccounts = this.sessionService.accounts?.map(
      (accountRelatedDTO) => {
        const accountUser = this.accountUsers?.find((currAccountUser) => {
          return currAccountUser.account_id === accountRelatedDTO.id;
        });

        return new UserAccount(accountRelatedDTO, accountUser);
      },
    );
  }

  initAccountUserForm(account: UserAccount) {
    this.selectedFacilities = account.selectedFacilities!;
    this.sendInternalEmails = account.sendInternalEmails!;
    this.accountUserForm = new FormGroup({
      role: new FormControl(account.role?.id, [Validators.required]),
      groups: new FormControl(account.selectedGroups ?? [], []),
      showFacilitiesRestriction: new FormControl(account.restrictingFacilities),
      nurtureSlideToggle: new FormControl(account.sendInternalEmails)
    });
    this.selectedGroupsObjArr = [];
    account.selectedGroups?.forEach((selectedGroup) => {
      const group = account.availableGroups.find(
        (currGroup) => currGroup.id === selectedGroup,
      );

      if (group) {
        this.selectedGroupsObjArr!.push(group);
      }
    });
    this.accountUserForm.controls.groups.valueChanges
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe((change) => {
        this.selectedGroupsObjArr = change?.map((selectedGroup: number) => {
          return account.availableGroups.find(
            (group) => group.id === selectedGroup,
          );
        });
      });

    // If value changes on any control, give user warning before the try closing the account user without saving
    this.accountUserForm.valueChanges
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe((change) => {
        this.accountUserFormChanged = true;
      });

    this.accountUserForm.controls.showFacilitiesRestriction.valueChanges
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe((change) => {
        if (change) {
          this.selectedFacilities = [];
        }
      });

    if (this.accountUserChangesDisabled) {
      this.disableAccountUserFields();
    }
  }

  initPreferencesForm() {
    this.userPreferencesForm = new FormGroup({
      firstName: new FormControl(this.user?.first_name, [Validators.required]),
      lastName: new FormControl(this.user?.last_name, [Validators.required]),
      company: new FormControl(this.user?.company),
      email: new FormControl(this.user?.email, [
        Validators.required,
        Validators.email,
      ]),
      timeZone: new FormControl(this.user?.timezone, [Validators.required]),
      super: new FormControl(this.user?.super),
    });
    if(this.showBasicRoleSelect){
      this.userPreferencesForm.addControl('basicRole', new FormControl(this.basicRole, [Validators.required]));
    }
  }

  saveUser() {
    const userDTO = this.getUserDTO();
    this.uiService
      .waitForEvent(
        this.usersService.updateUser(this.currentAccount.accountID, userDTO),
      )
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe(
        (result) => {
          // If edited user is current logged in user update the currentUserValue
          // TODO: RETEST THIS
          if (this.currentUser!.id === this.user?.id) {
            const newUserObj = {
              ...this.currentUser,
              ...(result as unknown as UserDTO),
            };

            this.sessionService.updateCurrentUser(newUserObj);
          }

          this.notificationService.openSuccessSnackBar(
            "User updated succesfully",
          );

          this.router.navigate([this.user!.id.toString(), "preferences"], {
            relativeTo: this.activatedRoute.parent!.parent,
            skipLocationChange: true,
          }).then(()=>{
            this.initForm(result);
          });

        },
        (err) => {
          this.notificationService.openErrorSnackBar("Error updating user");
        },
      );
  }

  createUser() {
    const userDTO = this.getUserDTO();
    this.uiService
      .waitForEvent(
        this.usersService.createUser(this.currentAccount.accountID, userDTO),
      )
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe(
        (result) => {
          this.notificationService.openSuccessSnackBar(
            "User created succesfully",
          );
          this.router.navigate([result.id.toString(), "preferences"], {
            relativeTo: this.activatedRoute.parent,
          });
        },
        (err) => {
          this.notificationService.openErrorSnackBar("Error creating user");
        },
      );
  }

  disableAccountUserFields() {
    this.accountUserForm.disable({ emitEvent: false });
  }

  onSuperClick() {
    if (!this.superSlideToggle.disabled) {
      this.userPreferencesForm.controls.super.setValue(
        !this.userPreferencesForm.controls.super.value,
      );
    }
  }

  onInternalEmailClicked() {
    this.accountUserForm.controls.nurtureSlideToggle.setValue(
      !this.accountUserForm.controls.nurtureSlideToggle.value,
    );
  }

  onFacilityToggled(facility: FacilityDTO) {
    if (!this.accountUserChangesDisabled) {
      if (!!this.selectedFacilities?.includes(facility.id)) {
        this.selectedFacilities = this.selectedFacilities.filter(
          (facilityID) => {
            return facilityID !== facility.id;
          },
        );
      } else {
        this.selectedFacilities?.push(facility.id);
      }
    }
  }

  onDeleteClick() {
    this.userDeleted = true;

    this.dialog
      .open(AreYouSureDialogComponent, {
        data: {
          areYouSureInfo: {
            description: `Are you sure you want to delete this user ( ${
              this.user!.email
            } )?`,
            warning: "Warning: this is not reversible!",
          },
        },
      })
      .afterClosed()
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe((result) => {
        if (result) {
          this.uiService
            .waitForEvent(
              this.usersService.deleteUser(
                this.currentAccount!.accountID,
                this.user!,
              ),
            )
            .pipe(takeUntil(this.onComponentDestroy))
            .subscribe(
              () => {
                this.router.navigate(["users", "list"]);
                this.notificationService.openSuccessSnackBar(
                  `Deleted user ${this.user!.email} successfully`,
                );
              },
              (err) => {
                this.userDeleted = false;
                const errorMessage = err.error.message ?? "Error deleting user";
                this.notificationService.openErrorSnackBar(errorMessage);
              },
            );
        } else {
          this.userDeleted = false;
        }
      });
  }

  onShowRestrictionsClicked() {
    if (this.accountUserForm.controls.showFacilitiesRestriction.enabled) {
      this.accountUserForm.controls.showFacilitiesRestriction.setValue(
        !this.accountUserForm.controls.showFacilitiesRestriction.value,
      );
    }
  }

  getUserDTO(): UserDTO {
    return {
      ...this.user!,
      first_name: this.userPreferencesForm?.controls.firstName.value,
      last_name: this.userPreferencesForm?.controls.lastName.value,
      company: this.userPreferencesForm?.controls.company.value,
      email: this.userPreferencesForm?.controls.email.value,
      timezone: this.userPreferencesForm?.controls.timeZone.value,
      disabled: this.user ? this.user.disabled : false,
      super: this.userPreferencesForm?.controls.super.value || false,
      basicRoleId: this.showBasicRoleSelect ? this.userPreferencesForm?.controls.basicRole.value.id : undefined
    };
  }

  goToChangePassword() {
    this.router.navigate(["users", "changePassword"]);
  }

  getEmailType(emailType: any) {
    return typeof emailType;
  }

  removeGroup(selectedGroup: any) {
    this.accountUserForm.controls.groups.setValue(
      this.accountUserForm.controls.groups.value.filter(
        (group: number) => group !== selectedGroup.id,
      ),
    );
  }

  get hasAccountUserChanged() {
    return (
      this.accountUserFormChanged ||
      this.selectedFacilities !== this.openedAccount?.selectedFacilities
    );
  }

  get saveDisabled() {
    return (
      this.userPreferencesForm?.invalid ||
      (!!this.openedAccount && this.hasAccountUserChanged)
    );
  }

  createAccountUser(userAccount: UserAccount) {
    const accountUserDTO = {
      account_id: userAccount.accountRelatedDTO.id,
      user_id: this.user!.id,
      restrict_to_facilities: null,
      send_internal_mail: false
    };
    userAccount.accountUser = new AccountUser(accountUserDTO as AccountUserDTO);

    setTimeout(() => {
      userAccount.expanded = true;
      this.cdRef.detectChanges();
    });
  }

  closeAccountUser() {
    if (this.hasAccountUserChanged) {
      this.dialog
        .open(AreYouSureDialogComponent, {
          data: {
            areYouSureInfo: {
              description: "You will lose any unsaved account information",
            },
          },
        })
        .afterClosed()
        .pipe(takeUntil(this.onComponentDestroy))
        .subscribe((result) => {
          if (result) {
            this.openedAccount!.expanded = false;
            this.accountUserFormChanged = false;
          }
        });
    } else {
      this.openedAccount!.expanded = false;
      this.accountUserFormChanged = false;
    }
  }

  saveAccountUser(userAccount: UserAccount) {
    const accountUserDTO = userAccount.accountUserDTO!;
    accountUserDTO.groups = this.accountUserForm.controls.groups.value;
    accountUserDTO.restrict_to_facilities = this.accountUserForm.controls
      .showFacilitiesRestriction.value
      ? this.selectedFacilities! ?? []
      : null;
    accountUserDTO.role_id = this.accountUserForm.controls.role.value;
    accountUserDTO.send_internal_mail = this.accountUserForm.controls.nurtureSlideToggle.value;

    if (userAccount.newAccountUser) {
      const createAccountUser = this.usersService.createAccountUser(
        accountUserDTO.account_id,
        accountUserDTO,
      );

      this.uiService
        .waitForEvent(createAccountUser)
        .pipe(takeUntil(this.onComponentDestroy))
        .subscribe((result: any) => {
          this.usersService
            .getUser(this.currentAccount.accountID, result.user_id)
            .pipe(takeUntil(this.onComponentDestroy))
            .subscribe((user) => {
              this.user = user as unknown as UserDTO;

              this.notificationService.openSuccessSnackBar(
                "Account User updated successfully",
              );

              this.openedAccount!.expanded = false;
              this.accountUserFormChanged = false;

              this.initAccountUsers();
            });
        });
    } else {
      const updateAccountUser = this.usersService.updateAccountUser(
        accountUserDTO.account_id,
        accountUserDTO.user_id,
        accountUserDTO,
      );

      updateAccountUser
        .pipe(takeUntil(this.onComponentDestroy))
        .subscribe((result: any) => {
          this.uiService
            .waitForEvent(
              this.usersService.getUser(
                this.currentAccount.accountID,
                result.user_id,
              ),
            )
            .pipe(takeUntil(this.onComponentDestroy))
            .subscribe((user) => {
              this.user = user as unknown as UserDTO;

              this.notificationService.openSuccessSnackBar(
                "Account User updated successfully",
              );

              this.openedAccount!.expanded = false;
              this.accountUserFormChanged = false;

              this.initAccountUsers();
            });
        });
    }
  }

  goToBilling(stripeId: string) {
    this.uiService
      .waitForEvent(
        this.accountService.getStripeCustomerSession(this.currentAccount.accountID, stripeId)
      )
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe(
        (result) => {
          window.open(result.url, "_blank")
        });
  }

  activate() {
    this.uiService
      .waitForEvent(
        this.usersService.activateUser(this.currentAccount.accountID, this.user!.id),
      )
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe(
        (result) => {
          
          this.notificationService.openSuccessSnackBar(
            "User activation email sent",
          );

          this.initForm(result);
          
        },
        (err) => {
          this.notificationService.openErrorSnackBar("Error sending user activation");
        },
      );
  }

}

class UserAccount {
  expanded = false;
  selectedGroups: number[] | undefined;
  newAccountUser: boolean;

  constructor(
    private _account: AccountRelatedDTO,
    private _accountUser?: AccountUser,
  ) {
    this.selectedGroups = this._accountUser?.groups?.map((group) => group.id);
    this.newAccountUser = !this._accountUser;
  }

  get name() {
    return this._account.name;
  }

  get hasAccountUser() {
    return !!this._accountUser;
  }

  get availableFacilities() {
    return this._account.facilities;
  }

  set availableFacilities(value) {
    this._account.facilities = value;
  }

  get availableGroups() {
    return this._account.accountUserGroups;
  }

  set availableGroups(value) {
    this._account.accountUserGroups = value;
  }

  get restrictingFacilities() {
    return !!this._accountUser?.restrictToFacilities;
  }

  get selectedFacilities() {
    return this._accountUser!.restrictToFacilities;
  }

  set selectedFacilities(value: number[] | null) {
    this._accountUser!.restrictToFacilities = value;
  }

  get accountRelatedDTO() {
    return this._account;
  }

  get accountUserDTO() {
    return this._accountUser?.accountUserDTO;
  }

  set accountUser(value: AccountUser) {
    this._accountUser! = value!;
  }

  get role() {
    return this._accountUser?.role;
  }

  get availableRoles() {
    return this._account.roles;
  }

  get sendInternalEmails() {
    return this._accountUser?.send_internal_mail;
  }

}
